// src/utils/runRefusal.js
//
// The run-refusal taxonomy: which failures of POST /api/v1/pipeline/run are a
// *verdict* about what this workspace may do, and what each one must say.
//
// Read at `a46bc40` on `origin/feature/stripe-payments`:
// `q/src/api/routes/pipeline.py` (`_REFUSAL_HTTP`, `_refuse`, the TENANT_RETIRED
// raise) and the guide's *Enforcement (PB4)* + *Error handling*.
//
// Three rules this module exists to hold:
//
//   1. **Branch on `error_code`, never on `message` and never on
//      `suggested_action`.** The latter is an open hint field, not an enum —
//      the backend's own comment says so — and CONTACT_SUPPORT covers both
//      NO_PLAN (a billing state) and LOOKUP_FAILED (a database blink).
//   2. **Only some of them are billing states the user can buy out of.**
//      LOOKUP_FAILED is a 503 the server invites you to retry, and
//      TENANT_RETIRED is an administrative act. Attaching an upgrade CTA to
//      either sells a product that fixes nothing. NO_ALLOWANCE is the subtle
//      one: there IS a plan and it is fine — its credit period was simply never
//      opened — so "upgrade" would sell a second plan to fix one not broken.
//   3. **Copy is resolved at RENDER time, not when the refusal is captured.**
//      A refusal descriptor is a plain, serialisable `{code, status, details}`
//      that lives in the store; `describeRefusal` turns it into words. Storing
//      the words instead would freeze them in whatever language was active when
//      the run failed.

import i18n from '../i18n/index.js';
import { formatRenewalDate } from './creditsFormat.js';

/**
 * The refusal codes we have copy for. Status and retryability come from the
 * server, not from this table — the table only says "we recognise this".
 *
 * ⚠️ The first FOUR are the values `block_reason` can take on /balance, so they
 * arrive by BOTH routes: predicted by the pre-flight, and again on a 402 the
 * pre-flight did not see. `LOOKUP_FAILED`, `TENANT_RETIRED`,
 * `FEATURE_NOT_IN_PLAN` and `APP_NOT_ASSIGNED` only ever arrive on the run.
 *
 * ⚠️ FEATURE_NOT_IN_PLAN (402) and APP_NOT_ASSIGNED (403) replaced the
 * withdrawn FORMAT_NOT_IN_PLAN on 2026-09-20. They are NOT interchangeable:
 * the first is an entitlement money can fix, the second is an operator act
 * (an application not assigned to this workspace) that no purchase repairs.
 */
export const RUN_REFUSAL_CODES = [
  'CREDITS_EXHAUSTED',
  'TRIAL_EXPIRED',
  'NO_PLAN',
  'NO_ALLOWANCE',
  // ⛔ SUBSCRIPTION_ENDED / SUBSCRIPTION_INACTIVE REMOVED 2026-09-24. They were
  // kept as dead-letter handling in case a 402 ever carried them. The billing
  // contract settles it: there is NO field saying a payment failed. While Stripe
  // retries nothing changes; when it gives up, `plan_id` becomes null and
  // /credits/balance reports NO_PLAN. ⛔ So NO_PLAN does not mean "cancelled" —
  // its copy is worded neutrally.
  'LOOKUP_FAILED',
  'TENANT_RETIRED',
  'FEATURE_NOT_IN_PLAN',
  'APP_NOT_ASSIGNED',
];

const KNOWN = new Set(RUN_REFUSAL_CODES);

/**
 * The four values `block_reason` can carry — the only ones the pre-flight sees.
 *
 * ⚠️ 2026-09-23: this set SHRANK. `SUBSCRIPTION_ENDED` and `SUBSCRIPTION_INACTIVE`
 * were withdrawn from the backend's `CreditReason` and can no longer arrive here;
 * `NO_ALLOWANCE` took their place. The live backend set is exactly:
 *   OK · EXEMPT · UNLIMITED · NO_PLAN · NO_ALLOWANCE · CREDITS_EXHAUSTED ·
 *   TRIAL_EXPIRED · LOOKUP_FAILED
 * (`<BACKEND_FOLDER>/src/content/billing_guard.py`, `CreditReason`), of which the
 * first three are ALLOWED verdicts and LOOKUP_FAILED is a 503 on the run.
 *
 * ⛔ NO_ALLOWANCE is NOT exhaustion: the plan is fine and its credit period was
 * never opened. Money cannot fix it — see the CTA table below.
 */
const BLOCK_REASONS = new Set([
  'CREDITS_EXHAUSTED',
  'TRIAL_EXPIRED',
  'NO_PLAN',
  'NO_ALLOWANCE',
]);

/**
 * Fallback copy key for a 402 whose code we do not know yet. The two codes this
 * slot was originally reserved for (SUBSCRIPTION_ENDED, SUBSCRIPTION_INACTIVE)
 * shipped, and were then withdrawn (2026-09-24); a 402 still carrying one lands
 * here. The slot stays for the next unknown code.
 */
const GENERIC_BLOCKED = 'BLOCKED';

// ⭐ The "billing link" const was deleted 2026-09-24: its only consumers were the
// CTAs of the two withdrawn codes above. /billing is always mounted now (the
// build-time billing flag was deleted too: the server answers 503
// STRIPE_NOT_CONFIGURED per request, more accurately than a flag could).

/**
 * Is this Error a run refusal, and if so what kind?
 *
 * @param {Error & {code?: string, status?: number, details?: object, isRetryable?: boolean}} err
 * @returns {{code: string, status: number|null, details: object|null, retryable: boolean, source: 'run'}|null}
 *   null when the failure is an ordinary error (a pipeline crash, a network
 *   drop) that the generic error path must keep handling.
 */
export function refusalFromError(err) {
  if (!err) return null;
  const code = err.code || '';
  const status = typeof err.status === 'number' ? err.status : null;

  // A 402 we do not recognise is still a payment verdict: render the generic
  // blocked copy rather than the raw server sentence — but invent no fields,
  // everything below is read off the envelope the server actually sent.
  if (!KNOWN.has(code) && status !== 402) return null;

  return {
    code: KNOWN.has(code) ? code : GENERIC_BLOCKED,
    status,
    details: err.details ?? null,
    // The server owns retryability; only LOOKUP_FAILED sets it today.
    retryable: err.isRetryable === true,
    source: 'run',
  };
}

/**
 * The pre-flight equivalent: a `block_reason` off /credits/balance, captured
 * before any request is sent.
 *
 * ⚠️ Driven by `can_generate` / `block_reason` ONLY. Never by the numbers:
 * `remaining_credits` is null for two opposite reasons (an unlimited plan that
 * may run forever, and no plan at all), so a null-based rule locks out exactly
 * the customers entitled to unlimited use.
 */
export function refusalFromBlockReason(blockReason) {
  if (!blockReason) return null;
  return {
    code: BLOCK_REASONS.has(blockReason) ? blockReason : GENERIC_BLOCKED,
    status: null,
    details: null,
    retryable: false,
    source: 'preflight',
  };
}

/**
 * CTA per refusal. The CTA is as much a part of the state as the copy: telling
 * a user on a plan-less workspace to "upgrade" points them at a control they do
 * not have, and telling a user whose credits ran out to "contact support" hides
 * the self-service remedy.
 *
 *   kind          | who it is for                                     | link
 *   --------------|---------------------------------------------------|--------------
 *   upgrade       | exhausted                                         | /billing
 *   upgrade       | output type not sold (FEATURE_NOT_IN_PLAN) and an | none
 *                 | unknown 402 (GENERIC_BLOCKED) — remedy not known  |
 *   choosePlan    | trial ended — terminal, needs a plan              | /billing
 *   contactAdmin  | no plan, retired workspace, application not       | none
 *                 | assigned — unfixable by the user                   |
 *   contactSupport| plan fine, credit period never opened (NO_ALLOWANCE)| none
 *   retry         | LOOKUP_FAILED — transient, not billing            | none
 *
 * ⛔ The /billing link (upgrade / choosePlan) is offered ONLY when the user can buy plans (`canBuyPlans`: a
 * company admin, or a superadmin outside admin mode). Anyone else gets `contactAdmin`, with no link.
 *
 * ⚠️ No refusal CTA POSTs to `/billing/portal-session` itself. The portal is a
 * customer-admin action with its own error paths, and it lives on the Piani
 * screen (/billing), which owns that button, that permission check and those
 * errors. Duplicating it in a notice that renders beside the Generate button
 * would give the same action two implementations and hand a member a control
 * the backend refuses. (The `renew` / `updatePayment` kinds that once linked
 * there went with SUBSCRIPTION_ENDED / SUBSCRIPTION_INACTIVE on 2026-09-24.)
 */
const CTA = {
  // ⭐ The most legitimate CTA in the product: the credits ran out and buying
  // more IS the remedy. ⚠️ /billing (not /credits) since v171 — the wallet page
  // shows what you have and what you spent; it cannot sell you anything.
  CREDITS_EXHAUSTED: { kind: 'upgrade', to: '/billing' },
  TRIAL_EXPIRED: { kind: 'choosePlan', to: '/billing' },
  NO_PLAN: { kind: 'contactAdmin', to: null },
  // ⛔⛔ NO upgrade, NO top-up, NO link. The plan is FINE — its credit period was
  // never opened, which is an operator/support fix. Before this row existed the
  // code fell through to GENERIC_BLOCKED, whose CTA was "upgrade" → /credits: the
  // product was selling a second plan to fix a plan that was not broken.
  // ⛔ Distinct from `contactAdmin` (NO_PLAN) on purpose — different audience:
  // an administrator can assign a plan, only support can open a credit period.
  NO_ALLOWANCE: { kind: 'contactSupport', to: null },
  // ⛔ NO LINK, deliberately. We cannot say WHICH plan includes the refused
  // output type, so a link to the catalogue leaves the user to work it out —
  // and the sentence already states the fact. ⏰ Revisit at the UI redesign,
  // when a plan card can say "available on Pro".
  FEATURE_NOT_IN_PLAN: { kind: 'upgrade', to: null },
  // ⛔ NO link: an operator must assign the application to this workspace.
  // Money cannot fix it, which is exactly why the backend makes it a 403
  // while the buyable refusals are 402.
  APP_NOT_ASSIGNED: { kind: 'contactAdmin', to: null },
  LOOKUP_FAILED: { kind: 'retry', to: null },
  TENANT_RETIRED: { kind: 'contactAdmin', to: null },
  // ⛔ NO LINK: an unknown 402. We do not know the remedy, so "upgrade" is a
  // guess — and guessing a remedy is how the product ended up selling a second
  // plan to fix a plan that was fine (see NO_ALLOWANCE above).
  [GENERIC_BLOCKED]: { kind: 'upgrade', to: null },
};

/**
 * Resolve a refusal descriptor into the words and the CTA to render.
 *
 * ⚠️ Namespace: the copy lives in `errors.json`, so it is addressed as
 * `errors:credits.refusal.*` through the i18n instance (`errors:` is the
 * NAMESPACE separator). The `credits.json` bundle is a different namespace
 * entirely, read as `t('...')` under `useTranslation('credits')`. Swapping the
 * two renders the raw key — which is why the tests assert on the rendered
 * sentence rather than on the key.
 *
 * @param {{code: string, status: number|null, details: object|null, retryable: boolean}} refusal
 * @param {{periodEnd?: string|null, canBuyPlans?: boolean}} [opts] `period_end` off /balance — used
 *   ONLY for the exhausted state's renewal date. It is deliberately not
 *   offered to TRIAL_EXPIRED: an elapsed trial is terminal and never renews.
 *   `canBuyPlans` (default false): may this user open /billing — without it no link is offered.
 * @returns {{code: string, title: string, body: string, cta: {kind: string, label: string, to: string|null}}}
 */
export function describeRefusal(refusal, { periodEnd, canBuyPlans = false } = {}) {
  const code = refusal && KNOWN.has(refusal.code) ? refusal.code : GENERIC_BLOCKED;
  const base = `errors:credits.refusal.${code}`;

  const parts = [i18n.t(`${base}.body`)];

  if (code === 'CREDITS_EXHAUSTED') {
    // The wallet belongs to the CUSTOMER and every workspace it owns spends
    // from it, so "you used all your credits" can be factually wrong about who
    // spent them. Say so before offering the upgrade.
    parts.push(i18n.t(`${base}.sharedWallet`));
    const date = formatRenewalDate(periodEnd);
    if (date) parts.push(i18n.t(`${base}.renewal`, { date }));
  }

  // ⛔ FEATURE_NOT_IN_PLAN deliberately adds NOTHING from `details`. The 402
  // carries `feature_id` (a BUILT-NEVER-PARSED id such as `cg:out:blog-seo`)
  // and `granted`, a list of PREFIXED ids with the plan's wildcard
  // unexpanded — rendering either would put `cg:out:…`, or a literal `*` on an
  // unlimited plan, in front of the user. The notice renders beside the option
  // that was just clicked, so naming it adds nothing anyway.

  const table = CTA[code] || CTA[GENERIC_BLOCKED];
  // ⛔ CLAUDE.md "Refusal CTAs link only where the user can act": /billing answers only a company admin (or a
  // superadmin outside admin mode). Anyone else is told who can fix it, with no link.
  const cta = table.to && !canBuyPlans ? { kind: 'contactAdmin', to: null } : table;
  return {
    code,
    title: i18n.t(`${base}.title`),
    body: parts.join(' '),
    cta: { ...cta, label: i18n.t(`errors:credits.refusal.cta.${cta.kind}`) },
  };
}

export default describeRefusal;
